import type { PrimaryAction, TaskCommand } from '@/features/tasks';

import type { NextTaskAction } from './entries';

/**
 * Every way to take a task's next step, as palette entries (feedback
 * 2026-10-06): the step it starts at, then the others it may start at; or
 * each way on from its gate, in the words its buttons used. Stop and Run
 * again have entries of their own.
 *
 * The switch has no default, so a new kind of next action fails the type
 * check here until the palette says what it offers for it.
 */
export function nextTaskActions(action: PrimaryAction): NextTaskAction[] {
  switch (action.kind) {
    case 'start':
      return [
        {
          act: 'task-start',
          stepId: action.target.stepId,
          label: `Start: ${action.target.label}`,
        },
        ...action.others.map((other) => ({
          act: 'task-start' as const,
          stepId: other.stepId,
          label: `Start at ${other.label}`,
        })),
      ];
    case 'decide':
      return action.outcomes.map((outcome) => ({
        act: 'task-decide' as const,
        stepId: outcome.to,
        label: outcome.label,
      }));
    case 'stop':
    case 'run-again':
    case 'none':
      return [];
  }
}

/**
 * The command a chosen next-step entry sends to the open drawer: its `act`
 * says start or decide, its `stepId` (the entry's `arg`) where to.
 */
export function nextTaskCommand(
  act: NextTaskAction['act'],
  stepId: string,
): Exclude<TaskCommand, string> {
  return act === 'task-start'
    ? { kind: 'start', stepId }
    : { kind: 'decide', to: stepId };
}
